import { Check } from 'lucide-react'
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@insightai/ui'
import { Link } from 'react-router-dom'
import { MarketingPage, Section } from './MarketingPage'

const TIERS = [
  {
    name: 'Self-serve',
    price: 'From ₹45,000',
    unit: 'per study',
    body: 'Secondary-only studies and small quantitative samples. Pay per study, no commitment.',
    features: [
      'TOSCA framing and issue tree',
      'Desk research with full provenance',
      'Research Lead design review',
      'PDF report with AI disclosure',
    ],
  },
  {
    name: 'Programme',
    price: 'Custom',
    unit: 'annual',
    highlight: true,
    body: 'Ongoing tracking, multi-market fieldwork and qualitative sessions with a named Research Lead.',
    features: [
      'Everything in Self-serve',
      'Quantitative fieldwork and cross-tabs',
      'Video focus groups and interviews',
      'Wave tracking with locked wording',
      'Knowledge graph across studies',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    unit: 'annual',
    body: 'For teams with existing standards, brand guidelines and integration requirements.',
    features: [
      'Everything in Programme',
      'API access and webhooks',
      'SSO and workspace governance',
      'Report branding',
    ],
  },
]

export function PricingPage() {
  return (
    <MarketingPage
      title="Pricing"
      lede="Every study is quoted as a min–max range with a breakdown before you commit. We do not publish a single number that quietly becomes a floor."
    >
      <ul className="grid gap-4 lg:grid-cols-3">
        {TIERS.map((tier) => (
          <li key={tier.name}>
            <Card
              className={tier.highlight ? 'h-full border-brand-primary' : 'h-full'}
              surface="raised"
            >
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-body">{tier.name}</CardTitle>
                  {tier.highlight && <Badge tone="brand">Most chosen</Badge>}
                </div>
                <p className="pt-2">
                  <span className="text-h1 tabular-nums">{tier.price}</span>{' '}
                  <span className="text-caption text-text-secondary">{tier.unit}</span>
                </p>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <p className="text-body-sm text-text-secondary">{tier.body}</p>
                <ul className="flex flex-col gap-1.5">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-body-sm">
                      <Check aria-hidden className="mt-0.5 size-3.5 shrink-0 text-state-verified" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Button variant={tier.highlight ? 'primary' : 'secondary'} asChild>
                  <Link to={tier.name === 'Self-serve' ? '/auth/signup' : '/resources#demo'}>
                    {tier.name === 'Self-serve' ? 'Start free brief' : 'Talk to us'}
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>

      <Section heading="How the estimate works">
        <p className="text-body-sm text-text-secondary">
          Cost is recalculated as you build the brief, and is always shown as a range with a
          breakdown explaining what drives the high end — sample size, market count, incidence rate
          and language coverage. The timeline is shown as a gauge alongside it. Both update within
          half a second of a change, so you can see the cost of a decision while you are making it.
        </p>
      </Section>
    </MarketingPage>
  )
}
